import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { productTransferService } from "~/services/purchasing";

import type {
  CreateProductTransferInput,
  FgPalletTransferInput,
  FgPlantTransferInput,
  UpdateProductTransferInput,
} from "~/types/productTransfer";

export const PRODUCT_TRANSFERS_KEY = ["product_transfers"];
export const PENDING_RECEIPTS_KEY = ["product_transfers", "pending_receipts"];
export const FG_PLANT_TRANSFERS_KEY = ["fg_plant_transfers"];

// Every stock surface a transfer send/receive/cancel can touch.
function invalidateTransferSurfaces(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: PRODUCT_TRANSFERS_KEY });
  queryClient.invalidateQueries({ queryKey: FG_PLANT_TRANSFERS_KEY });
  queryClient.invalidateQueries({ queryKey: ["product_lotes_for_transfer"] });
  queryClient.invalidateQueries({ queryKey: ["stock"] });
  queryClient.invalidateQueries({ queryKey: ["position-inventory"] });
  queryClient.invalidateQueries({ queryKey: ["warehouse-inventory"] });
  queryClient.invalidateQueries({ queryKey: ["wms-movements"] });
  queryClient.invalidateQueries({ queryKey: ["wms-kardex"] });
  queryClient.invalidateQueries({ queryKey: ["products"] });
}

/**
 * List all producto-terminado transfers (bodega B1 <-> B2 <-> NC), newest first.
 */
export function useProductTransfers() {
  return useQuery({
    queryKey: PRODUCT_TRANSFERS_KEY,
    queryFn: () => productTransferService.listTransfers(),
  });
}

/**
 * List all finished-good between-plants transfers (P1 <-> P2 <-> NC, TF-#####),
 * newest first. Surfaced alongside the other transfer kinds in the Traslados history.
 */
export function useFgPlantTransfers() {
  return useQuery({
    queryKey: FG_PLANT_TRANSFERS_KEY,
    queryFn: () => productTransferService.listFgPlantTransfers(),
  });
}

/**
 * Transfers still in transit (sent, pending the receiving bodega's confirmation).
 */
export function usePendingReceipts() {
  return useQuery({
    queryKey: PENDING_RECEIPTS_KEY,
    queryFn: () => productTransferService.listPendingReceipts(),
  });
}

/**
 * Active bodegas for the origin/destination selects.
 */
export function useWarehouses() {
  return useQuery({
    queryKey: ["wms_warehouses", "active"],
    queryFn: () => productTransferService.listWarehouses(),
  });
}

/**
 * The selected product's unit of measure (CAJA/UNI/LTS/…), used to label the
 * transfer quantity. Cached generously — a product's unit rarely changes.
 */
export function useProductUnit(productId: string | null) {
  return useQuery({
    queryKey: ["product_unit", productId],
    queryFn: () => productTransferService.getProductUnit(productId as string),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Available lotes of a product per bodega (with unreserved box counts).
 */
export function useProductLotesForTransfer(productId: string | null) {
  return useQuery({
    queryKey: ["product_lotes_for_transfer", productId],
    queryFn: () => productTransferService.getLotes(productId as string),
    enabled: !!productId,
  });
}

/**
 * Finished ("Listo") un-ingested pallets of a still-open batch at a plant — the
 * checklist for a partial-during-production traslado. Enabled only for a plant lote
 * that is pallet-backed (source = 'plant_pallets').
 */
export function useBatchReadyPallets(
  batchId: string | null,
  plant: string | null
) {
  return useQuery({
    queryKey: ["batch_ready_pallets", batchId, plant],
    queryFn: () =>
      productTransferService.getBatchReadyPallets(
        batchId as string,
        plant as string
      ),
    enabled: !!batchId && !!plant,
  });
}

/**
 * Send a PT transfer (stock leaves origin into transit). On success, invalidates
 * the transfer lists plus the WMS stock surfaces the move touched.
 */
export function useCreateProductTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: CreateProductTransferInput) =>
      productTransferService.createTransfer(vars),
    onSuccess: () => {
      invalidateTransferSurfaces(queryClient);
      queryClient.invalidateQueries({ queryKey: PENDING_RECEIPTS_KEY });
    },
  });
}

/**
 * Move a batch's un-ingested (pending) finished-good stock between plants
 * (P1 <-> P2 <-> NC). Relocates pending-ingestion quantity; net-zero on stock.
 * Also refreshes the WMS ingestion queue, which reads those pending requests.
 */
export function useTransferFgBetweenPlants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: FgPlantTransferInput) =>
      productTransferService.transferFgBetweenPlants(vars),
    onSuccess: () => {
      invalidateTransferSurfaces(queryClient);
      queryClient.invalidateQueries({ queryKey: ["wms_ingestion_requests"] });
    },
  });
}

/**
 * Move whole finished PALLETS of a still-open production batch between plants
 * (P1 <-> P2 <-> NC): the partial-during-production traslado. Net-zero on stock;
 * refreshes the lote list + the batch's ready-pallet checklist so a repeated
 * partial transfer sees the reduced availability.
 */
export function useTransferFgPalletsBetweenPlants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: FgPalletTransferInput) =>
      productTransferService.transferFgPalletsBetweenPlants(vars),
    onSuccess: () => {
      invalidateTransferSurfaces(queryClient);
      queryClient.invalidateQueries({ queryKey: ["batch_ready_pallets"] });
    },
  });
}

/**
 * Confirm reception of an in-transit transfer (the "CHECK"): deposits the stock
 * into the destination bodega. Idempotent.
 */
export function useReceiveProductTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { transferId: string; performedBy: string }) =>
      productTransferService.receiveTransfer(vars.transferId, vars.performedBy),
    onSuccess: () => {
      invalidateTransferSurfaces(queryClient);
      queryClient.invalidateQueries({ queryKey: PENDING_RECEIPTS_KEY });
    },
  });
}

/**
 * Cancel an in-transit transfer: returns the stock to the origin bodega.
 */
export function useCancelProductTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { transferId: string; performedBy: string }) =>
      productTransferService.cancelTransfer(vars.transferId, vars.performedBy),
    onSuccess: () => {
      invalidateTransferSurfaces(queryClient);
      queryClient.invalidateQueries({ queryKey: PENDING_RECEIPTS_KEY });
    },
  });
}

/**
 * Edit an in-transit transfer's quantity + notes before reception. Adjusts the
 * origin box delta, then invalidates the transfer lists + WMS stock surfaces.
 */
export function useUpdateProductTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateProductTransferInput) =>
      productTransferService.updateTransfer(vars),
    onSuccess: () => {
      invalidateTransferSurfaces(queryClient);
      queryClient.invalidateQueries({ queryKey: PENDING_RECEIPTS_KEY });
    },
  });
}
